import { comparisonData } from '@/mocks/homeData';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
          <i className={`text-xs ${i < rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
        </span>
      ))}
    </div>
  );
}

export default function ComparisonSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
          {/* Left content */}
          <div className="lg:w-1/2">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-foreground-950 leading-tight mb-6">
              Why itaobuyspreadsheet<br />Outperforms Other<br />Systems
            </h2>

            {/* Stats row */}
            <div className="flex items-center gap-6 md:gap-10 mb-6">
              <div className="flex-1">
                <p className="text-3xl md:text-4xl font-heading text-accent-500">99%+</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Data Accuracy</p>
              </div>
              <div className="w-px h-10 bg-background-300"></div>
              <div className="flex-1">
                <p className="text-3xl md:text-4xl font-heading text-accent-500">10+</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Categories Covered</p>
              </div>
              <div className="w-px h-10 bg-background-300"></div>
              <div className="flex-1">
                <p className="text-3xl md:text-4xl font-heading text-accent-500">300%+</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Efficiency Boost</p>
              </div>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed max-w-md">
              Unlike traditional manual lists or basic Google Sheets, the{' '}
              <strong className="text-foreground-800">itaobuyspreadsheet</strong> system offers automated comparisons, real-time price tracking, and intelligent category organization — all in one structured framework.
            </p>
          </div>

          {/* Right decorative area */}
          <div className="lg:w-1/2 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square bg-accent-50/50 rounded-2xl overflow-hidden">
              <div className="absolute top-8 left-8 w-24 h-24 rounded-xl bg-accent-100/60"></div>
              <div className="absolute top-20 right-12 w-20 h-20 rounded-full bg-accent-200/40"></div>
              <div className="absolute bottom-12 left-16 w-32 h-16 rounded-xl bg-accent-100/50"></div>
              <div className="absolute bottom-8 right-8 w-16 h-16 rounded-full bg-accent-200/30"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-6xl font-heading text-accent-400/40">★</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison table */}
        <div className="mt-12 md:mt-16 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-background-200/70">
                <th className="text-left py-3 px-4 text-sm font-medium text-foreground-600">System</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-foreground-600">Speed</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-foreground-600">Accuracy</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-foreground-600">Automation</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-foreground-600">SEO Popularity</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-background-100 bg-accent-50/30">
                <td className="py-3.5 px-4">
                  <span className="text-sm font-semibold text-foreground-950">itaobuyspreadsheet</span>
                </td>
                {comparisonData.rows.slice(0, 4).map((row, ri) => (
                  <td key={ri} className="text-center py-3.5 px-4">
                    <StarRating rating={row.itaobuy} />
                  </td>
                ))}
              </tr>
              <tr className="border-b border-background-100">
                <td className="py-3.5 px-4">
                  <span className="text-sm text-foreground-700">Google Sheets</span>
                </td>
                {comparisonData.rows.slice(0, 4).map((row, ri) => (
                  <td key={ri} className="text-center py-3.5 px-4">
                    <StarRating rating={row.googleSheets} />
                  </td>
                ))}
              </tr>
              <tr className="border-b border-background-100">
                <td className="py-3.5 px-4">
                  <span className="text-sm text-foreground-700">Notion Templates</span>
                </td>
                {comparisonData.rows.slice(0, 4).map((row, ri) => (
                  <td key={ri} className="text-center py-3.5 px-4">
                    <StarRating rating={row.notion} />
                  </td>
                ))}
              </tr>
              <tr>
                <td className="py-3.5 px-4">
                  <span className="text-sm text-foreground-700">Manual Lists</span>
                </td>
                {comparisonData.rows.slice(0, 4).map((row, ri) => (
                  <td key={ri} className="text-center py-3.5 px-4">
                    <StarRating rating={row.manual} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-foreground-500 mt-3 px-4">
            * Based on independent analysis and user-reported data. The <strong>itaobuyspreadsheet</strong> system represents a new generation of data-driven shopping optimization.
          </p>
        </div>
      </div>
    </section>
  );
}